import clsx from 'clsx';
import styled from '@emotion/styled';
import { Intent } from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import tw from 'twin.macro';
import React, { Fragment, Children, cloneElement } from 'react';

interface Props {
  fill?: boolean;
  large?: boolean;
  vertical?: boolean;
  intent?: Intent;
  className?: string;
  children?: React.ReactNode;
  outlined?: boolean;
  minimal?: boolean;
  alignText?: 'left' | 'right' | 'center';
}

const flattenChildren = (children: any) => {
  const result: any[] = [];
  children.map(child =>
    child.type === Fragment
      ? result.push(
          ...flattenChildren(Children.toArray(child?.props?.children)),
        )
      : result.push(child),
  );
  return result;
};

// Si je vire le popover il faut penser à changer ici
const StyledButtonGroup = styled.span`
  &:not(.tsh-vertical) {
    &:not(.tsh-minimal) {
      > .tsh-tooltip:not(:first-child) .tsh-button,
      > .tsh-button:not(:first-child) {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }

      > .tsh-menu:not(:first-child) .tsh-button,
      > .tsh-button:not(:first-child) {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }

      > .tsh-menu:not(:last-child) .tsh-button,
      > .tsh-button:not(:last-child) {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
        margin-right: -1px;
      }

      > .tsh-tooltip:not(:last-child) .tsh-button,
      > .tsh-button:not(:last-child) {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
        margin-right: -1px;
      }
    }
  }

  &.tsh-vertical {
    &.tsh-fill {
      height: 100%;
      width: unset;
    }

    .tsh-button {
      // we never want that margin-right when vertical
      margin-right: 0 !important; /* stylelint-disable-line declaration-no-important */
      // needed to ensure buttons take up the full width when wrapped in a Popover:
      width: 100%;
    }

    &:not(.tsh-minimal) {
      > .tsh-menu:first-child .tsh-button,
      > .tsh-button:first-child {
        border-radius: 0.5rem 0.5rem 0 0;
      }

      > .tsh-menu:not(:last-child) .tsh-button,
      > .tsh-button:not(:last-child) {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      }

      > .tsh-menu:not(:first-child) .tsh-button,
      > .tsh-button:not(:first-child) {
        border-top-right-radius: 0;
        border-top-left-radius: 0;
      }

      > .tsh-menu:last-child .tsh-button,
      > .tsh-button:last-child {
        border-radius: 0 0 0.5rem 0.5rem;
      }

      > .tsh-menu:not(:last-child) .tsh-button,
      > .tsh-button:not(:last-child) {
        margin-bottom: -1px;
      }

      > .tsh-tooltip:first-child .tsh-button,
      > .tsh-button:first-child {
        border-radius: 0.5rem 0.5rem 0 0;
      }

      > .tsh-tooltip:not(:last-child) .tsh-button,
      > .tsh-button:not(:last-child) {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      }

      > .tsh-tooltip:not(:first-child) .tsh-button,
      > .tsh-button:not(:first-child) {
        border-top-right-radius: 0;
        border-top-left-radius: 0;
      }

      > .tsh-tooltip:last-child .tsh-button,
      > .tsh-button:last-child {
        border-radius: 0 0 0.5rem 0.5rem;
      }

      > .tsh-tooltip:last-child .tsh-button,
      > .tsh-button:last-child:first-child {
        border-radius: 0.5rem 0.5rem 0.5rem 0.5rem;
      }

      > .tsh-tooltip:not(:last-child) .tsh-button,
      > .tsh-button:not(:last-child) {
        margin-bottom: -1px;
      }
    }
  }
`;

export default function ButtonGroup(props: Props) {
  const {
    fill,
    alignText,
    children,
    vertical,
    large,
    className,
    outlined,
    minimal,
    intent,
  } = props;
  const flatChildren = flattenChildren(Children.toArray(children));
  return (
    <StyledButtonGroup
      css={[
        tw`inline-flex relative z-0 rounded-md`,
        tw`overflow-hidden`, // without this, nested buttons overflow the rounded corners
        vertical && tw`flex-col`,
        fill && tw`w-full`,
        intent && !outlined && tw`divide-x`,
      ]}
      className={clsx({ 'tsh-vertical': vertical }, className)}
    >
      {flatChildren.map(child => {
        if (typeof child.type === 'string') {
          return child;
        }
        return cloneElement(child, {
          fill,
          outlined: child?.props?.outlined || outlined,
          minimal: child?.props?.minimal || minimal,
          intent: child?.props?.intent || intent,
          large: child?.props?.large || large,
          alignText: child?.props?.alignText || alignText,
        });
      })}
    </StyledButtonGroup>
  );
}
